// src/encounter/lib/visit-time.ts
// The in-encounter header time label (Flutter add_encounter_detail_widget.dart:268-272 `roomInfo`):
//   "${isToday ? 'Today' : scDateFormat(date, 'MMM dd')}, ${scTimeFormatSingleLetter(date)}"
// e.g. "Today, 10:00 AM" or "Jun 18, 2:30 PM". We keep the repo's AM/PM marker (not Flutter's
// single-letter a/p) to match src/shared/lib/date-format.ts (a deliberate, documented divergence).
// `now` is injectable so the same-day check is deterministic in tests.

import { practiceNow } from '@/shared/lib/practice-time';

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/**
 * Format an appointment start as the encounter-header time label. Returns '' for
 * null / undefined / unparseable input so the caller can hide the block.
 */
export function formatVisitTime(iso: string | null | undefined, now: Date = practiceNow()): string {
  if (iso == null || iso === '') return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const day = isSameDay(d, now) ? 'Today' : d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
  return `${day}, ${time}`;
}

/**
 * The note header's date + time stamp — Flutter's `"Sep 07, 2026 • 8:45a"`.
 *
 * Flutter composes its second header line as `location • scDateFormat(startTime) •
 * scTimeFormatSingleLetter(startTime)` (`generated_notes_widget.dart:129`), with `scDateFormat`
 * defaulting to `'MMM dd, yyyy'` (`utils.dart:176`) and `scTimeFormatSingleLetter` to `'h:mma'`
 * with `AM -> a` / `PM -> p` (`utils.dart:264-273`). The caller joins `[visitType, date]` on
 * ` • ` (`generated-note-body.tsx:324`), so this returns the `date • time` half and the two
 * compose to Flutter's whole line. Flutter's literal ends in a TRAILING SPACE; that is dropped.
 *
 * DELIBERATE DIVERGENCE FROM `formatVisitTime` ABOVE: that one keeps the repo's `AM`/`PM` marker
 * (see the file header). This one does NOT — the note is the surface Flutter's single-letter
 * meridiem was measured on, so `8:45a`, lower case, with no space before it. Do not "restore" it.
 *
 * The time half is computed by hand rather than through `toLocaleTimeString`: modern ICU emits a
 * NARROW NO-BREAK SPACE (U+202F) before AM/PM, so a `.replace(' AM', 'a')` would silently stop
 * matching. Full year rather than `formatVisitTime`'s "Today" — a note is a record and should not
 * read relative to when it is being viewed.
 *
 * Returns undefined for null / undefined / unparseable input, so the caller drops the segment
 * rather than stamping a fabricated "now" on a clinical note.
 */
export function formatNoteStamp(iso: string | null | undefined): string | undefined {
  if (iso == null || iso === '') return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  const date = d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  const hours24 = d.getHours();
  const hour = hours24 % 12 || 12;
  const minute = String(d.getMinutes()).padStart(2, '0');
  const meridiem = hours24 < 12 ? 'a' : 'p';
  return `${date} • ${hour}:${minute}${meridiem}`;
}
